import { type VehiclePassageHistoryApiRequest, type VehiclePassageHistoryApiResponse, VehiclePassageHistoryRequester } from '@cobliteam/api-lib';
import GeofenceRequester from '@cobliteam/api-lib/src/dashboard/geofence/GeofenceRequesterV3';

import type { PageableContent } from '@cobliteam/common-types';
import type { GeofenceFilter, GeofenceResume, PlainGeofence } from '@cobliteam/common-types/src/geofenceV3';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import {
  type DefinedInitialDataInfiniteOptions,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { saveAs } from 'file-saver';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getGeofenceRequester = () => new GeofenceRequester({ host: env.geofenceApiHost });
const getVehiclePassageHistoryRequester = () => new VehiclePassageHistoryRequester({ host: env.apiHost });
export const QUERY_KEY: QueryKey = ['GEOFENCES_V3'];

const getGeofencesLimitErrorMessage = (error: any, defaultMessage: string) => {
  // TODO: wait for backend to return a better structure for errors
  const maxGeofencesByFleetErrorMessage = 'Total of geofences exceeded the limit by fleet';
  const maxAddressesPerRequestErrorMessage = 'Total items per request exceeded the limit';
  const emptySpreadsheet = "The uploaded batch doesn't follow the specified limitations.";
  const maximumComplexity = 'maximum complexity reached by the polygon, you must use a maximum of 40 points';

  if (error.response?.data?.errors?.error?.includes(maxGeofencesByFleetErrorMessage)) {
    return Alert.error('Limite de 5000 locais de interesse excedido. Remova um local de interesse para adicionar um novo.');
  }

  if (error.response?.data?.errors?.error?.includes(maxAddressesPerRequestErrorMessage)) {
    return Alert.error('Limite de 100 locais de interesse por planilha excedido.');
  }

  if (error.response?.data?.errors?.error?.includes(emptySpreadsheet)) {
    return Alert.error('Nenhum endereço foi encontrado na planilha.');
  }

  if (error.response?.data?.errors?.error?.includes(maximumComplexity)) {
    return Alert.error('Falha ao salvar local. Desenhe um local de no máximo 40 pontos no mapa.');
  }

  return Alert.error(defaultMessage);
};

const DEFAULT_GEOFENCES_STALE_TIME_MS = 60_000;

const useGetAllGeofences = (params?: { options?: Partial<UseQueryOptions<PlainGeofence[] | undefined>>; filters?: GeofenceFilter } | null) => {
  const options = params?.options;
  const filters = params?.filters;
  return useQuery({
    queryKey: [...QUERY_KEY, 'LIST', filters],
    queryFn: async () => {
      const response = await getGeofenceRequester().getGeofences({
        filters: { ...filters, page: 0, unpaged: true },
      });
      return response.content;
    },
    staleTime: DEFAULT_GEOFENCES_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    ...options,
  });
};

const useExportGeofences = () => {
  return useMutation({
    mutationFn: async () => {
      try {
        const response = await getGeofenceRequester().exportGeofences();
        saveAs(response, 'geofences.csv');
        return response;
      } catch (error) {
        Alert.error('Ops! Houve um erro ao exportar os locais de interesse. Por favor, tente novamente.');
        throw error;
      }
    },
  });
};

const useGetPaginatedGeofences = (params?: { options?: Partial<UseQueryOptions<PageableContent<PlainGeofence>>>; filters?: GeofenceFilter } | null) => {
  const options = params?.options;
  const filters = params?.filters;
  return useQuery({
    queryKey: [...QUERY_KEY, 'PAGINATED_LIST', filters],
    queryFn: async () => {
      const response = await getGeofenceRequester().getGeofences({
        filters: { ...filters },
      });
      return response;
    },
    ...options,
  });
};

const useInfinityGeofences = ({ filters, options }: { filters: GeofenceFilter; options?: Partial<DefinedInitialDataInfiniteOptions<PageableContent<PlainGeofence>>> }) => {
  return useInfiniteQuery<PageableContent<PlainGeofence>>({
    initialPageParam: 0,
    queryKey: [...QUERY_KEY, 'LIST', filters],
    queryFn: ({ pageParam }) => {
      const currentPage = typeof pageParam === 'number' ? pageParam : 0;

      return getGeofenceRequester().getGeofences({
        filters: {
          ...filters,
          page: currentPage,
          sort: filters.sort?.concat({ column: 'id', order: 'asc' }),
        },
      });
    },
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.last === false) return pages.length;
      return undefined;
    },
    ...options,
  });
};

const useGetGeofenceById = (id: string, options?: Partial<UseQueryOptions<PlainGeofence>>) => {
  return useQuery<PlainGeofence>({
    ...options,
    queryKey: [...QUERY_KEY, id],
    queryFn: () => getGeofenceRequester().getGeofenceById(id),
    meta: {
      errorMessage: 'Falha ao carregar local de interesse por ID. Tente novamente mais tarde.',
    },
  });
};

const useGetGeofenceResume = (options?: Partial<UseQueryOptions<GeofenceResume[]>>) => {
  return useQuery<GeofenceResume[]>({
    queryKey: [...QUERY_KEY, 'MAP_POINTS'],
    queryFn: () => getGeofenceRequester().getGeofenceResume({ forceVisibility: false }),
    meta: {
      errorMessage: 'Falha ao carregar resumo dos locais de interesse no mapa. Tente novamente mais tarde.',
    },
    ...options,
  });
};

// This query is used to get the geofences with forced visibility to show on map
const useGetGeofenceForcedVisibility = (options?: Partial<UseQueryOptions<PlainGeofence[]>>) => {
  return useQuery<PlainGeofence[]>({
    queryKey: [...QUERY_KEY, 'MAP_POINTS', 'FORCED_VISIBILITY'],
    queryFn: () => getGeofenceRequester().getGeofenceForcedVisibility(),
    meta: {
      errorMessage: 'Falha ao carregar locais de interesse (forced visibility) no mapa. Tente novamente mais tarde.',
    },
    ...options,
  });
};

const useCreateGeofence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: getGeofenceRequester().createGeofence,
    onSuccess: async () => {
      Alert.success('Local de interesse criado com sucesso!');
    },
    onError: (error: AxiosError) => {
      getGeofencesLimitErrorMessage(error, 'Ops! Falha ao criar local de interesse. Por favor, tente novamente mais tarde.');
      throw error;
    },
    onSettled: async () => {
      queryClient.refetchQueries({ queryKey: [...QUERY_KEY, 'PAGINATED_LIST'] });
      queryClient.refetchQueries({ queryKey: [...QUERY_KEY, 'MAP_POINTS'] });
      queryClient.refetchQueries({ queryKey: [...QUERY_KEY, 'LIST'] });
    },
  });
};

const useUpdateGeofence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: getGeofenceRequester().updateGeofence,
    onMutate: async (plainGeofence) => {
      queryClient.cancelQueries({
        queryKey: [...QUERY_KEY, 'MAP_POINTS'],
      });
      queryClient.cancelQueries({ queryKey: [...QUERY_KEY, 'LIST'] });
      queryClient.cancelQueries({
        queryKey: [...QUERY_KEY, plainGeofence.id],
      });

      // update geofence resume cache on map
      const previousGeofenceMapPoints = queryClient.getQueryData([...QUERY_KEY, 'MAP_POINTS']);
      queryClient.setQueryData([...QUERY_KEY, 'MAP_POINTS'], (old: GeofenceResume[] | undefined) => {
        if (!old) return old;
        return old.map((geofence) => {
          if (geofence.id === plainGeofence.id) {
            return {
              id: plainGeofence.id,
              lat: plainGeofence.geometryPoint.coordinates[1],
              lng: plainGeofence.geometryPoint.coordinates[0],
              type: plainGeofence.type,
            };
          }
          return geofence;
        });
      });

      // update geofence id cache
      const previousGeofence: any = queryClient.getQueryData([...QUERY_KEY, plainGeofence.id]);

      const previousList: PageableContent<PlainGeofence> | undefined = queryClient.getQueryData([...QUERY_KEY, 'LIST']);
      return {
        previousList,
        plainGeofence,
        previousGeofence,
        previousGeofenceMapPoints,
      };
    },
    onSuccess: (_data, _variables, context: any) => {
      Alert.success('Local de interesse atualizado!');
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY] });
      queryClient.setQueryData([...QUERY_KEY, context?.plainGeofence.id], {
        ...context?.previousGeofence,
        ...context?.plainGeofence,
      });
    },
    onError: (error: AxiosError, _variables, context: any) => {
      getGeofencesLimitErrorMessage(error, 'Erro ao tentar atualizar um local de interesse!');
      queryClient.setQueryData([...QUERY_KEY, context?.previousGeofence.id], context?.previousGeofence);
      queryClient.setQueryData([...QUERY_KEY, 'MAP_POINTS'], context?.previousGeofenceMapPoints);
      queryClient.setQueryData([...QUERY_KEY, 'LIST'], context?.previousList);
    },
  });
};

const useDeleteGeofence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: getGeofenceRequester().deleteGeofence,
    onSuccess: () => {
      Alert.success('Local de interesse removido!');
    },
    onError: () => {
      Alert.error('Ops! Houve um erro ao deletar o local de interesse. Vamos atualizar a lista e tente novamente.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, 'PAGINATED_LIST'] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, 'MAP_POINTS'] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, 'LIST'] });
    },
  });
};

const useValidateImportGeofences = () => {
  return useMutation({
    mutationFn: getGeofenceRequester().validateImportGeofences,
    onError: (error: AxiosError) => {
      getGeofencesLimitErrorMessage(error, 'Ops! Falha ao criar local de interesse. Por favor, tente novamente mais tarde.');
    },
  });
};

const useImportGeofences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: getGeofenceRequester().importGeofences,
    onSuccess: () => {
      Alert.success('Locais de interesse importados com sucesso!');
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY, 'LIST'],
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY, 'MAP_POINTS'],
      });
    },
    onError: () => {
      Alert.error('Ops! Houve um erro ao importar os endereços da planilha. Por favor, tente novamente.');
    },
  });
};

const useGetEstados = ({ options }: { options?: Partial<UseQueryOptions<any[]>> } = {}): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: [...QUERY_KEY, 'ESTADOS'],
    queryFn: () => fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados').then((res) => res.json()),
    ...options,
  });
};

const useGetMunicipios = (estadoId?: string, options?: Partial<UseQueryOptions<any[]>>): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: [...QUERY_KEY, 'MUNICIPIOS', estadoId],
    queryFn: () => fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${estadoId}/municipios`).then((res) => res.json()),
    ...options,
    enabled: !!estadoId,
  });
};

const useGetGeofenceByEstado = (estadoId?: string, options?: Partial<UseQueryOptions<GeoJSON.FeatureCollection | null>>): UseQueryResult<GeoJSON.FeatureCollection | null> => {
  return useQuery({
    queryKey: [...QUERY_KEY, 'ESTADO', estadoId],
    queryFn: () =>
      fetch(`https://servicodados.ibge.gov.br/api/v4/malhas/estados/${estadoId}?formato=application/vnd.geo+json&resolucao=5&qualidade=maxima`).then((res) => res.json()),
    ...options,
    enabled: !!estadoId,
  });
};

const useGetGeofenceByMunicipio = (
  municipioId?: string,
  options?: Partial<UseQueryOptions<GeoJSON.FeatureCollection | null>>
): UseQueryResult<GeoJSON.FeatureCollection | null> => {
  const getGeoData = async (municipioId?: string) => {
    if (!municipioId) return null;
    try {
      const geoJsonResponse = await fetch(`https://servicodados.ibge.gov.br/api/v4/malhas/municipios/${municipioId}?formato=application/vnd.geo+json&resolucao=5&qualidade=maxima`);
      const geoJsonData = await geoJsonResponse.json();
      return geoJsonData;
    } catch (_error) {
      return null;
    }
  };

  return useQuery({
    queryKey: [...QUERY_KEY, 'MUNICIPIO', municipioId],
    queryFn: () => getGeoData(municipioId),
    ...options,
    enabled: !!municipioId,
  });
};

const useInfinityVehiclePassageHistory = ({ filters, options }: { filters: VehiclePassageHistoryApiRequest; options?: any }) => {
  return useInfiniteQuery<VehiclePassageHistoryApiResponse>({
    initialPageParam: 0,
    queryKey: [...QUERY_KEY, 'PASSAGE', filters],
    queryFn: ({ pageParam = 0 }: { pageParam: number }) =>
      getVehiclePassageHistoryRequester().load({
        distanceRadiusInMeters: filters.distanceRadiusInMeters,
        latitude: filters.latitude,
        longitude: filters.longitude,
        startDate: filters.startDate,
        endDate: filters.endDate,
        page: pageParam,
        size: 10,
        timezone: filters.timezone,
      }),
    getNextPageParam: (lastPage, pages) => {
      const loadedItems = pages
        .flat()
        .map((response) => response.rows?.length ?? 0)
        .reduce((acc, a) => acc + a);

      if (lastPage.count === loadedItems) return undefined;
      return pages.flat().length;
    },
    ...options,
  });
};

export {
  useCreateGeofence,
  useDeleteGeofence,
  useGetAllGeofences,
  useExportGeofences,
  useGetEstados,
  useGetGeofenceByEstado,
  useGetGeofenceById,
  useGetGeofenceByMunicipio,
  useGetGeofenceForcedVisibility,
  useGetGeofenceResume,
  useGetMunicipios,
  useGetPaginatedGeofences,
  useImportGeofences,
  useInfinityGeofences,
  useInfinityVehiclePassageHistory,
  useUpdateGeofence,
  useValidateImportGeofences,
};
